import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState, useEffect, useCallback } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { DoctorTypeBadge } from '../shared';
import { DOCTOR_TYPE, DOCTOR_TYPE_LABELS } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';
import { history } from '../../../Helpers/history';

// ─── Privilege label helper ────────────────────────────────────────────────

const PRIVILEGE_LABELS = {
  laparoscopy: 'Laparoscopy',
  open_surgery: 'Open Surgery',
  thyroid: 'Thyroid',
  ortho_trauma: 'Ortho Trauma',
  arthroplasty: 'Arthroplasty',
  gynaecology: 'Gynaecology',
  obstetric_surgery: 'Obstetric Surgery',
  spine: 'Spine',
};

const formatPrivilege = (p) => PRIVILEGE_LABELS[p] || p.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Styles ────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#333',
  },
  registerBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 20px',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  searchField: {
    minWidth: 280,
  },
  typeSelect: {
    minWidth: 180,
  },
  resultsCount: {
    marginLeft: 'auto',
    color: '#666',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  tableWrapper: {
    padding: '0 4px 12px',
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  headCell: {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  nameCell: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#333',
  },
  specialtyCell: {
    fontSize: '0.85rem',
    color: '#555',
  },
  privilegeChip: {
    marginRight: 4,
    marginBottom: 2,
    height: 22,
    fontSize: '0.72rem',
  },
  statusDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  statusText: {
    fontSize: '0.85rem',
    verticalAlign: 'middle',
  },
  viewLink: {
    fontSize: '0.85rem',
    color: '#1976d2',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  emptyState: {
    padding: 40,
    textAlign: 'center',
    color: '#999',
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: 60,
  },
}));

// ─── Component ─────────────────────────────────────────────────────────────

const DoctorRegistryPage = () => {
  const classes = useStyles();

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const loadDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await surgeryService.searchDoctors({ query: searchQuery || undefined });
      const data = res?.data || res;
      setDoctors(data.doctors || []);
    } catch (err) {
      console.error('Failed to load doctors:', err);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  // Client-side type filter
  const filteredDoctors = typeFilter === 'all'
    ? doctors
    : doctors.filter((d) => d.type === typeFilter);

  const handleRowClick = (doctorId) => {
    history.push(`/surgery/doctors/${doctorId}`);
  };

  return (
    <div>
      <PageTitle title="Doctor Registry" />
      <SubHeader title="Surgery" subTitle="Doctor Registry" />

      <div className={classes.root}>
        {/* Header */}
        <div className={classes.headerRow}>
          <Typography className={classes.headerTitle}>Doctor Registry</Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            className={classes.registerBtn}
          >
            Register New Doctor
          </Button>
        </div>

        {/* Filter Bar */}
        <Paper elevation={1} className={classes.filterBar}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Search by name or specialty..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" size="small" className={classes.typeSelect}>
            <Label>Doctor Type</Label>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Doctor Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value={DOCTOR_TYPE.INTERNAL}>
                {DOCTOR_TYPE_LABELS[DOCTOR_TYPE.INTERNAL]}
              </MenuItem>
              <MenuItem value={DOCTOR_TYPE.VISITING_CONSULTANT}>
                {DOCTOR_TYPE_LABELS[DOCTOR_TYPE.VISITING_CONSULTANT]}
              </MenuItem>
              <MenuItem value={DOCTOR_TYPE.LOCUM}>
                {DOCTOR_TYPE_LABELS[DOCTOR_TYPE.LOCUM]}
              </MenuItem>
              <MenuItem value={DOCTOR_TYPE.REFERRED_SPECIALIST}>
                {DOCTOR_TYPE_LABELS[DOCTOR_TYPE.REFERRED_SPECIALIST]}
              </MenuItem>
            </Select>
          </FormControl>
          <Typography className={classes.resultsCount}>
            {filteredDoctors.length} doctor{filteredDoctors.length !== 1 ? 's' : ''}
          </Typography>
        </Paper>

        {/* Table */}
        <Paper elevation={1} className={classes.tableWrapper}>
          {loading ? (
            <div className={classes.loadingWrapper}>
              <CircularProgress size={32} />
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body1">No doctors found</Typography>
              <Typography variant="body2" style={{ marginTop: 4, color: '#aaa' }}>
                Try adjusting your search or filter criteria
              </Typography>
            </div>
          ) : (
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headCell}>Name</TableCell>
                  <TableCell className={classes.headCell}>Specialty</TableCell>
                  <TableCell className={classes.headCell}>Type</TableCell>
                  <TableCell className={classes.headCell}>Privileges</TableCell>
                  <TableCell className={classes.headCell}>Status</TableCell>
                  <TableCell className={classes.headCell} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDoctors.map((doctor) => (
                  <TableRow
                    key={doctor.id}
                    className={classes.tableRow}
                    onClick={() => handleRowClick(doctor.id)}
                  >
                    <TableCell className={classes.nameCell}>{doctor.name}</TableCell>
                    <TableCell className={classes.specialtyCell}>{doctor.speciality}</TableCell>
                    <TableCell>
                      <DoctorTypeBadge doctorType={doctor.type} />
                    </TableCell>
                    <TableCell>
                      {(doctor.privileges || []).map((p) => (
                        <Chip
                          key={p}
                          label={formatPrivilege(p)}
                          size="small"
                          variant="outlined"
                          className={classes.privilegeChip}
                          style={{ borderColor: '#4CAF50', color: '#4CAF50' }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: doctor.available ? '#4CAF50' : '#F44336' }}
                      />
                      <span className={classes.statusText}>
                        {doctor.available ? 'Available' : 'Unavailable'}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        to={`/surgery/doctors/${doctor.id}`}
                        className={classes.viewLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        View Profile
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </div>
    </div>
  );
};

export default DoctorRegistryPage;
